import {
  Card,
  Chip,
  ChipGroup,
  DataListItem,
  ExpansionPanel,
  Divider,
  List,
  Tag,
  Skeleton,
  Tab,
  TabGroup,
  Text,
  Grid,
  GridItem,
  Button,
  Modal,
} from '../../../../components'
import { HomeScheduleBar, TemperatureBar } from '../index'
import {
  ThermostatDailySchedule,
  ThermostatSchedule,
} from '@/types/thermostatSchedule'
import {
  CostRange,
  ThermostatComfortSetting,
} from '@/types/thermostatComfortSetting'
import { toDegrees, toDollars } from '../../../../utils/string'
import { useState } from 'react'

export type ThermostatDailySchedulesWithDisplayValues = {
  dailySchedules: ThermostatDailySchedule[]
  comfortSettingCosts: {
    comfortSetting: ThermostatComfortSetting
    cost: number
    costRange: CostRange
  }[]
  totalCost: number
  totalCostRange: CostRange
}

export interface Props extends ThermostatSchedule {
  // No additional props provided
  schedules: ThermostatDailySchedulesWithDisplayValues[]
  total_cost_range: CostRange
  monthName: string
  onChipGroupClick?: (index: number | null) => void
  loading: boolean
}

function formatDollars(dollars: number, decimals = 2) {
  return toDollars(dollars, { format: 'dollars', decimals: decimals })
}

const ZERO_COST_RANGE: CostRange = { low: 0, high: 0 }

function formatDollarRange(range: CostRange) {
  const low = formatDollars(range.low, 0)
  const high = formatDollars(range.high, 0)
  return low === high ? low : `${low} – ${high}`
}

const SheetSkeleton = () => (
  <Skeleton animation="wave" variant="text" width={34} height={26} />
)

const TemperatureSkeleton = () => (
  <Skeleton animation="wave" variant="text" width={25} height={18} />
)

const TemperatureBarSkeleton = () => (
  <Skeleton animation="wave" variant="text" width={137} height={18} />
)

function ComfortSettingPanelLabel(props: {
  label: string
  heat: number
  cool: number
  costRange: CostRange
  loading: boolean
}) {
  return (
    <Grid
      fluid
      gap="12px"
      placeItems="center"
      placeContent="space-between"
      templateColumns="auto minmax(min-content, 200px)"
    >
      <Text>{props.label}</Text>
      <Grid
        placeItems="center start"
        templateColumns="1fr auto"
        style={{
          fontVariantNumeric: 'tabular-nums',
          width: '100%',
        }}
      >
        <Grid gap="6px" templateColumns="26px 2px 26px">
          <Text className="text-themed-red-500">
            {props.loading ? <TemperatureSkeleton /> : toDegrees(props.heat)}
          </Text>
          <Divider orientation="vertical" />
          <Text className="text-themed-blue-500">
            {props.loading ? <TemperatureSkeleton /> : toDegrees(props.cool)}
          </Text>
        </Grid>
        {props.loading ? (
          <SheetSkeleton />
        ) : (
          <GridItem>
            <Tag color="themed-grey-200">
              <Text>{formatDollarRange(props.costRange)}</Text>
            </Tag>
          </GridItem>
        )}
      </Grid>
    </Grid>
  )
}

function DayOfWeekPanelLabel(props: {
  costRange: CostRange
  loading: boolean
}) {
  return (
    <Grid placeItems="center" gap="8px">
      Daily Cost
      {props.loading ? (
        <SheetSkeleton />
      ) : (
        <Tag color="themed-grey-200">
          <Text>{formatDollarRange(props.costRange)}</Text>
        </Tag>
      )}
    </Grid>
  )
}

function SettingUsageListItem(props: { label: string; costRange: CostRange }) {
  return (
    <DataListItem label={props.label}>
      <Grid templateColumns="auto">
        <GridItem placeSelf="end">
          <Text variant="body2">{formatDollarRange(props.costRange)}</Text>
        </GridItem>
      </Grid>
    </DataListItem>
  )
}

function SettingCostListItem(props: {
  label: string
  heat: number
  cool: number
  costRange: CostRange
}) {
  return (
    <DataListItem label={props.label}>
      <Grid
        templateColumns="1fr auto"
        style={{
          fontVariantNumeric: 'tabular-nums',
          width: '177px',
          maxWidth: '100%',
        }}
      >
        <Grid gap="6px" templateColumns="26px 2px 26px">
          <Text className="text-themed-red-500">{toDegrees(props.heat)}</Text>
          <Divider orientation="vertical" />
          <Text className="text-themed-blue-500">{toDegrees(props.cool)}</Text>
        </Grid>
        <GridItem>
          <Text variant="body2">{formatDollarRange(props.costRange)}</Text>
        </GridItem>
      </Grid>
    </DataListItem>
  )
}

function HowWeEstimateModal() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <Modal
      title="How are costs estimated?"
      id="thermostat-schedule-how-we-estimate-modal"
      open={isModalOpen}
      onOpen={() => setIsModalOpen(true)}
      onClose={() => setIsModalOpen(false)}
    >
      <Modal.Activator>
        <Text variant="link" id="thermostat-schedule-how-we-estimate-button">
          How we estimate
        </Text>
      </Modal.Activator>
      <Text variant="body2">
        We estimate each cost from your past heating and cooling activity, how
        much power your system draws, and your utility&apos;s rate at that time
        of day. The more accurate your home details (system size, square
        footage, year built), the closer it gets.
      </Text>
      <Modal.Actions>
        <Button
          id="thermostat-schedule-how-we-estimate-close-button"
          onClick={() => setIsModalOpen(false)}
          variant="primary"
        >
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

function EstimateExplainer() {
  return (
    <Grid flow="row" gap="4px" placeItems="start">
      <Text variant="body2" className="mt-2 text-themed-grey-500">
        This is how much we estimate your preferred schedule costs each month.
        These are estimates, not exact charges.
      </Text>
      <HowWeEstimateModal />
    </Grid>
  )
}

function AvgOutsideTempListItem(props: {
  loading: boolean
  high: number
  low: number
  max: number
  min: number
}) {
  return (
    <div className="bg-themed-grey-100 rounded-lg px-4 min-h-[48px] flex items-center justify-between [&_.bar.md]:bg-themed-grey-200">
      <Text variant="body2">Avg. Outside Temp</Text>
      <Grid>
        {props.loading ? (
          <TemperatureBarSkeleton />
        ) : (
          <TemperatureBar
            high={props.high}
            low={props.low}
            max={props.max}
            min={props.min}
          />
        )}
      </Grid>
    </div>
  )
}

function TotalRow(props: {
  label: string
  loading: boolean
  costRange: CostRange
}) {
  return (
    <div className="flex items-center justify-between min-h-[48px] px-1">
      <Text>{props.label}</Text>
      {props.loading ? (
        <SheetSkeleton />
      ) : (
        <Tag color="themed-grey-200">
          <Text>{formatDollarRange(props.costRange)}</Text>
        </Tag>
      )}
    </div>
  )
}

function getNext12Months() {
  const calendarMonths = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]
  const date = new Date()
  const months = []

  // "Current" month is set to 2 months from now to account for `This Month` and `Next Month` as options
  date.setMonth(date.getMonth() + 2)
  let month = date.getMonth()

  for (let i = 0; i < 10; i++) {
    months.push(date.getMonth())
    month++
    date.setMonth(month)
  }

  return months.map((index) => calendarMonths[index])
}

export default function HomeScheduleCard(props: Props) {
  return (
    <Card>
      <Grid gap="24px" flow="row">
        <Text variant="h3">Estimated Monthly Costs</Text>
        <Grid gap="32px" flow="row">
          <ChipGroup
            disabled={props.loading}
            centered
            required
            scrollable
            onClick={props.onChipGroupClick}
          >
            <Chip>
              <Text>This Month</Text>
            </Chip>
            <Chip>
              <Text>Next Month</Text>
            </Chip>
            {getNext12Months().map((month, index) => {
              return (
                <Chip key={index}>
                  <Text>{month}</Text>
                </Chip>
              )
            })}
          </ChipGroup>
          <TabGroup centered>
            <Tab title="Comfort Setting">
              <Grid flow="row" gap="20px">
                <EstimateExplainer />
                <AvgOutsideTempListItem
                  loading={props.loading}
                  high={props.avg_high_temp_f}
                  low={props.avg_low_temp_f}
                  max={props.max_temp_f}
                  min={props.min_temp_f}
                />
                <Grid flow="row" gap="24px">
                  <Grid flow="row" gap="20px">
                    {(props.comfort_settings || []).map((setting, index) => {
                      return (
                        <ExpansionPanel
                          disabled={props.loading}
                          key={index}
                          label={
                            <ComfortSettingPanelLabel
                              loading={props.loading}
                              label={setting.name}
                              heat={setting.heat_setpoint}
                              cool={setting.cool_setpoint}
                              costRange={
                                setting.total_cost_range ?? ZERO_COST_RANGE
                              }
                            />
                          }
                        >
                          <List bordered>
                            {(setting.schedules || []).map(
                              (schedule, index) => {
                                return (
                                  <SettingUsageListItem
                                    costRange={
                                      schedule.cost_range ?? ZERO_COST_RANGE
                                    }
                                    key={index}
                                    label={schedule.label}
                                  />
                                )
                              },
                            )}
                          </List>
                        </ExpansionPanel>
                      )
                    })}
                  </Grid>
                  <Divider orientation="horizontal" />
                </Grid>
                <TotalRow
                  label={`${props.monthName} Cost Estimate`}
                  loading={props.loading}
                  costRange={props.total_cost_range}
                />
              </Grid>
            </Tab>
            <Tab title="Day of Week">
              <Grid gap="20px" flow="row">
                <EstimateExplainer />
                <AvgOutsideTempListItem
                  loading={props.loading}
                  high={props.avg_high_temp_f}
                  low={props.avg_low_temp_f}
                  max={props.max_temp_f}
                  min={props.min_temp_f}
                />
                <Grid flow="row" gap="48px">
                  {props.schedules.map((schedule, index) => {
                    return (
                      <Grid flow="row" gap="24px" key={index}>
                        <HomeScheduleBar
                          schedule={schedule.dailySchedules}
                          settings={props.comfort_settings}
                        />
                        <ExpansionPanel
                          disabled={props.loading}
                          label={
                            <DayOfWeekPanelLabel
                              costRange={schedule.totalCostRange}
                              loading={props.loading}
                            />
                          }
                        >
                          <List bordered>
                            {schedule.comfortSettingCosts.map(
                              ({ comfortSetting, costRange }, index) => (
                                <SettingCostListItem
                                  key={index}
                                  costRange={costRange}
                                  cool={comfortSetting.cool_setpoint}
                                  label={comfortSetting.name}
                                  heat={comfortSetting.heat_setpoint}
                                />
                              ),
                            )}
                          </List>
                        </ExpansionPanel>
                      </Grid>
                    )
                  })}
                </Grid>
                <Grid gap="12px" flow="row">
                  <Divider orientation="horizontal" />
                  <TotalRow
                    label={`${props.monthName} Cost Estimate`}
                    loading={props.loading}
                    costRange={props.total_cost_range}
                  />
                </Grid>
              </Grid>
            </Tab>
          </TabGroup>
        </Grid>
      </Grid>
    </Card>
  )
}
